<script setup>
import { computed, onBeforeUnmount, onUnmounted, onMounted, ref, watch, reactive, nextTick } from 'vue';
import {useRouter} from 'vue-router';
import activeGameEditor from '@/components/activeGameEditor.vue';
// import memorySquares from './components/memorySquares';
import crosswordPuzzle from '@/components/Crossword.vue';
import wordHunt from '@/components/WordHunt.vue';
import wordCards from '@/components/WordCards.vue';
import fillInBlank from '@/components/FillInBlank.vue';
import teamScores from '@/components/TeamScores.vue';
import { useTriviaStore, useWebSocketStore } from '@/stores/trivia.js';
import { darkenColor, lightenColor, hexToRgba } from '@/util/FormatStyles.js';

const store = useTriviaStore();
const router = useRouter();
const webSocketStore = useWebSocketStore();
const { initWebSocket, connectionStatus, serverMessage, sendMessage } = webSocketStore;
const messageContent = ref('');


onMounted(() => {
  store.clientType = 'presenter';
  initWebSocket();
  setTimeout(() => {
    store.resetQuestions();
    sendMessage({ status: 'Presenter Connected', gameId: store.CurrentGame._id ?? null });
  }, 1500);
});



import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
const editor = ClassicEditor;
const editorConfig = ref({});


/*
tools => display scores, answers, or question editor
generators => general reference for app use

questions is an array
each item in the array is an object (and represents one round of gameplay)
each 'round' object is number-keyed, and each key is a question
each question is an object that looks like this
{ cat: string     category of question to display
  timeAllotted: number
  q: array, containing question segments
  a: object with text, highlight (number tells the app which multiple choice answer to highlight)
  hint object (for special question types, like wordLeap)
}

The question's q array is filled with object segments,
where the type of question segment (ie text/image) is set,
as well as all the info needed to display that segment. 
*/

const getInitialTools = () => ({
  scores: false,
  answers: false,
  questions: false
});

const getInitialGenerators = () => ({
  wordLeap: false,
  sizeMatters: false,
  boxOffice: false,
  bank:[],
  seed: null,
  highlightedWords: [],
  promptInput: null,
  manager: false,
  showRound: null,
  showQuestion: null,
});

const activeRound = computed ({
  get() {
    return store.CurrentGame.activeRound;
  },
  set(newVal) {
    store.CurrentGame.activeRound = newVal;
  }
});

const activeQuestion = computed ({
  get() {
    return store.CurrentGame.activeQuestion;
  },
  set(newVal) {
    store.CurrentGame.activeQuestion = newVal;
  }
});


const showInterlude = computed({
  get() {
    return store.CurrentGame.interlude.show;
  },
  set(newVal) {
    store.CurrentGame.interlude.show = newVal;
  }
})

const tools = ref(getInitialTools());
const generators = ref(getInitialGenerators());


const questionStep = computed(() => store.CurrentGame.questionStep);
const questionSubStep = computed(() => store.CurrentGame.questionSubStep);

const currentQuestion = computed({
  get() {
    try {
      return store.CurrentGame.rounds[activeRound.value][activeQuestion.value];
    }
    catch {
      return {};
    }
  },
  set(newVal) {
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value] = newVal;
  }
});

function updateQuestions(newQs) {
  console.log('updating disabled');
  // questions.value = newQs;
}


// Sets an item with a Key to local storage
function saveStorage(key, data) {
  console.log('saving', key);
  localStorage.setItem(key, JSON.stringify(data));
};

// Looks for a local storage item and returns if present
function getStorage(key, item) {
    if( localStorage.getItem(key) && item) {
        const data = JSON.parse(localStorage.getItem(key))
        return data[item]
    }
    else if(localStorage.getItem(key)) {
      return localStorage.getItem(key)
    }
};

// onMounted(() => {
//   // clearStorage();
//   if (getStorage('teams')) {
//     teams.value = JSON.parse(getStorage('teams'));
//   }
//   // if (getStorage('questions')) {
//   //   questions.value = JSON.parse(getStorage('questions'));
//   // }
// });

// Clear a single item or the whole local storage
function clearStorage(key) {
    if(key) {
        localStorage.removeItem(key);
    } else {
        localStorage.clear();
    }
}


const questions = computed ({
  get() {
    return store.CurrentGame.rounds;
  },
  set(newVal) {
    store.CurrentGame.rounds = newVal;
  }
})

const questionOverride = ref(null);


function multipleChoiceDisplay(segment, originalPosition) {
  const currentQ = questions.value[activeRound.value][activeQuestion.value];
  const dataAnswers = currentQ.segments[segment] ? currentQ.segments[segment].dataAnswers : [];
  let list = [...currentQ.segments[segment].data];
  if (list.length && showingAnswers.value && dataAnswers) {
    const listWithAnswers = [];
    for (let a = 0; a < list.length; a += 1) {
      let updated = dataAnswers[a] ? `${list[a]} (${dataAnswers[a]})` : list[a];
      listWithAnswers.push(updated);
    }
    list = listWithAnswers;
  }
  if (!showingAnswers.value || originalPosition) {
    return list;
  }
  // Re-order lists when answer is shown.
  const unordered = list;
  const reordered = [];
  console.log('reordering');
  if (currentQ.answerData.reorder && currentQ.answerData.reorder.length) {
    const newOrder = currentQ.answerData.reorder.split(',');
    for (let a = 0; a < newOrder.length; a += 1) {
      reordered.push(unordered[newOrder[a] - 1]);
    }
    return reordered;
  }
  return list;
};

function multipleChoiceClass(segment, choice, index) {
  // Returns original index for coloring multiple choice segments when they've been reordered.
  const currentQ = questions.value[activeRound.value][activeQuestion.value];
  let wrongAnswer;
  if ((currentQ.answerData.highlight.length && currentQ.answerData.highlight.length > 2)
  || (showingAnswers.value && currentQ.answerData.reorder.length)) {
    const highlights = [];
    currentQ.answerData.highlight.forEach(i => highlights.push(Number(i)));
    wrongAnswer = highlights.indexOf(index) === -1
  } else {
    wrongAnswer = currentQ.answerData.highlight.indexOf(index) === -1;
  }
  if (showingAnswers.value && (!currentQ.answerData.reorder || currentQ.answerData.reorder.length === 0) && wrongAnswer) {
    return 'incorrect';
  }
  if (!(showingAnswers.value && currentQ.answerData.reorder.length)) {
    return index;
  }
  return multipleChoiceDisplay(segment, true).indexOf(choice);
}


function segmentClass(segType) {
  let segmentClass = 'question-segment';
  if (segType === 'image') {
    segmentClass = 'question-segment__image';
  }
  if (segType === 'clueAnswer') {
    segmentClass = 'question-segment__no-background';
  }
  return segmentClass;
}

const showingAnswers = computed(() => {
  console.log('showing answers', store.CurrentGame.showAnswer);
  return store.CurrentGame.showAnswer;
});


const teams = ref([]);


function nextQuestion() {
  if (activeQuestion.value + 1 < questions.value[activeRound.value].length) {
    store.setQuestion(activeQuestion.value + 1);
  } else {
    nextRound();
  }
}

const categoryIntro = computed({
  get() {
    return store.showCategoryIntro;
  },
  set(newVal) {
    store.showCategoryIntro = newVal;
  }
});
const categoryIntroLength = ref(3000);

function showCategoryIntro() {
  categoryIntro.value = true;
  setTimeout(() => {
      categoryIntro.value = false;
  }, categoryIntroLength.value);
}




function previousQuestion() {
  if (questions.value[activeRound.value][activeQuestion.value - 1]) {
    store.setQuestion(activeQuestion.value - 1);
  } else {
    previousRound();
  }
}


function nextRound() {
  if (questions.value[activeRound.value + 1]) {
    store.setRound(activeRound.value + 1, 0);
  }
}

function previousRound() {
  if (activeRound.value && activeRound.value !== 0) {
    store.setRound(activeRound.value - 1, 0);
  }
  activeQuestion.value = 0;
}




let timerStarted = ref(null);
// let mediaPlaying = ref(false);


function questionStepForward() {
  quickEdit.value = null;
  categoryIntro.value = false;
  if (showInterlude.value) {
    store.setInterlude(false);
    showCategoryIntro();
  } else if (currentQuestion.value.segments) {
    store.advanceQuestion();
  }
}


// const timeLeftWidth = ref('100%');
// const timeUsedWidth = ref('0%');
const totalTime = computed({
  get() {
    return Number(store.CurrentGame.timeLeft) || 0;
  },
  set(newVal) {
    store.setTimer({ timeLeft: newVal });
  }
});
let timeLeft = ref(0);
let countdownTimer = ref(null);

// Background colors for timer



const timeLeftInMinutes = computed(() => {
  let timeRemaining;
  if (store.CurrentGame.timeLeft) {
    const minutes = Math.floor(store.CurrentGame.timeLeft / 60);
    const seconds = store.CurrentGame.timeLeft % 60;
    if (minutes) timeRemaining = `<b>${minutes}</b><small>min</small> <b>${seconds}</b><small>sec</small>`;
    else timeRemaining = `<b>${seconds}</b><small>sec</small>`;
  }
  return timeRemaining;
});

let timeLeftStyle = 'cornflowerblue';
let timeUsedStyle = 'rgba(35,49,125,1)';
const timeRemaining = computed(() => {
  return store.CurrentGame.timeLeft;
});

const progressBarStyle = computed(() => {
  if (store.readyToRun) {
    // let timeLeft = timeLeftStyle;
    // let timeUsed = timeUsedStyle;
    const timeLeft = timeRemaining.value;
    const totalTime = store.CurrentGame.rounds
    && store.CurrentGame.rounds[activeRound.value]
    && store.CurrentGame.rounds[activeRound.value][activeQuestion.value]
    ? store.CurrentGame.rounds[activeRound.value][activeQuestion.value].timeAllotted : 0;
    const timeUsed = totalTime - timeLeft;
    let color = null;
    let question = null;
    if (questions.value[activeRound.value] && questions.value[activeRound.value][activeQuestion.value]) {
      question = questions.value[activeRound.value][activeQuestion.value];
    }
    if (question && question.format) {
        if (question.format.timerFullBackground) {
          timeLeftStyle = question.format.timerFullBackground;
        }
        if (question.format.timerEmptyBackground) {
          timeUsedStyle = question.format.timerEmptyBackground;
        }
        if (question.format.categoryText) {
          color = question.format.categoryText;
        }
    }
    return `background: linear-gradient(90deg, ${timeUsedStyle} 0%, ${timeUsedStyle} ${(timeUsed / totalTime) * 100}%, ${timeLeftStyle} ${(timeUsed / totalTime) * 100}%), ${timeLeftStyle} 100%;${color ? `color: ${color};` : ''}`;
  }
});

const wordLeapSample = [
  'CHOPPY','WATER','PRESSURE','POINT','BLANK','SCREEN','SAVER'
  ];
const wordWiseDemo = new URL('../assets/demos/wordwise.png', import.meta.url);
const showDemo = ref(false);
const hintHasBeenShown = ref(0);
const hint = ref({});

function showHint() {
  // const hint = currentQuestion.a.hint.amount;
  // Find the segment with a hint function.

  // WordLeap handling
  const i = currentQuestion.value.segments.findIndex(i => i.type === 'wordLeap');
  let numberOfWords = (currentQuestion.value.segments[i].data.length - 2);
  let numberOfHints;
  for (let a = 0; a < numberOfWords; a += 1) {
    if (!hint.value[a + 1]) {
      hint.value[a + 1] = [];
    }
    let hintValue = Math.floor((Math.random() * currentQuestion.value.segments[i].data[a + 1].split('').length - 1) + 1);
    while (hintValue === 0 || hint.value[a + 1].indexOf(hintValue) > -1) {
      hintValue = Math.floor((Math.random() * currentQuestion.value.segments[i].data[a + 1].split('').length - 1) + 1); // First letter is already displayed, so display second instead.
    }
    hint.value[a + 1].push(hintValue);
  }
}



const transitionCategory = ref(false);
const hideQuestion = ref(false);

function transitionAnswer() {
  transitionCategory.value = true;
  setTimeout(() => {
    transitionCategory.value = false;
  }, 850);
}
function transitionTrigger() {
  hideQuestion.value = true;
  setTimeout(() => {
    hideQuestion.value = false;
  }, 150);
}

function resetTools() {
  tools.value = getInitialTools();
}
function resetGenerators() {
  generators.value = getInitialGenerators();
}

function closeTools() {
  resetTools();
  resetGenerators();
  showDemo.value = false;
}

function clueAnswerStyle(segment, type, index) {
  const position = type === 'clue' ? 1 : 0;
  const even = index % 2 === 0;
  let style = `flex: ${segment.indexSplit[position]};`;
  if (segment.magnify) {
    style += `font-size: ${segment.magnify}em;`;
    style += `line-height: ${segment.magnify}em;`;
  }
  const shouldApplyFullBorderRadius = segment.hideIndex || !(segment.forceAnswer && !showingAnswers.value);
  const conditionalBorderRadius = !segment.hideIndex || (segment.forceAnswer && showingAnswers.value);
  if (shouldApplyFullBorderRadius) {
    style += 'border-radius: 25px;';
  }
  if (!segment.reverseIndex) {
    if (conditionalBorderRadius) {
      style += position === 0 ? 'border-radius: 25px 0 0 25px;' : 'border-radius: 0 25px 25px 0;';
    }
  } else {
    if (conditionalBorderRadius) {
      style += position === 0 ? 'border-radius: 0 25px 25px 0;' : 'border-radius: 25px 0 0 25px;';
    }
  }

  const highlightsPresent = [];
  segment.data.forEach((d, i) => {
    if (d.highlightOnAnswer) {
      highlightsPresent.push(i);
    }
  });
  const qFormat = store.CurrentGame.rounds[activeRound.value][activeQuestion.value].format;
  
  let wrongAnswer = false;
  if (highlightsPresent.length && showingAnswers.value && !segment.data[index].highlightOnAnswer) {
    wrongAnswer = true;
  }

  if (type === 'clue') {
    style += `color: ${!wrongAnswer ? qFormat.clueColor : hexToRgba(qFormat.clueColor, 0.65)};`;
    if (!even && segment.columns === 1) {
      style += `background: ${!wrongAnswer ? darkenColor(qFormat.clueBackground, 10) : hexToRgba(darkenColor(qFormat.clueBackground, 10), 0.3)};`;
    } else {
      style += `background: ${!wrongAnswer ? qFormat.clueBackground : hexToRgba(qFormat.clueBackground, 0.3)};`;
    }
  } else {
    style += `color: ${!wrongAnswer ? qFormat.answerColor : hexToRgba(qFormat.answerColor, 0.65)};`;
    if (!even && segment.columns === 1) {
      style += `background: ${!wrongAnswer ? darkenColor(qFormat.answerBackground, 10) : hexToRgba(darkenColor(qFormat.answerBackground, 10),0.3)};`;
    } else {
      style += `background: ${!wrongAnswer ? qFormat.answerBackground : hexToRgba(qFormat.answerBackground,0.3)};`;
    }
  }
  console.log('style', style);
  // Update colors

  // if (q.data[index].format && q.data[index].format[type]) {
    
  //   // if (q.data[index].format[type].theme
  //   // && q.data[index].format[type].theme.title
  //   // && q.data[index].format[type].theme.title !== 'Custom') {
  //   //   style += `color: ${q.data[index].format[type].theme.color};`;
  //   // // } else {
  //   // //   style += `color: ${q.data[index].format[type].color};`;
  //   // }
  //   // if (q.data[index].format[type].theme
  //   // && q.data[index].format[type].theme.title
  //   // && q.data[index].format[type].theme.title !== 'Custom') {
  //   //   style += `background: ${q.data[index].format[type].theme.background};`;
  //   // } else {
  //   //   style += `background: ${q.data[index].format[type].background};`;
  //   // }

  //     // if (position === 0 && q.data[index].format.index.color) {
  //     //   style += `color: ${q.data[index].format.index.color};`;
  //     // }
  //     // if (position === 0 && q.data[index].format.index.background) {
  //     //   style += `background: ${q.data[index].format.index.background};`;
  //     // }
  //     // if (position === 0 && showingAnswers && q.data[index].format.answer.color) {
  //     //   style += `color: ${q.data[index].format.answer.color};`;
  //     // }
  //     // if (position === 0 && showingAnswers && q.data[index].format.answer.background) {
  //     //   style += `background: ${q.data[index].format.answer.background};`;
  //     // }
  // }
  // Check if any highlightOnAnswers are present
  
  return style;
}

const categoryText = computed(() => {
  if (store.CurrentGame.interlude && store.CurrentGame.interlude.show && store.CurrentGame.interlude.text) {
    if (!store.CurrentGame.interlude.text.perRound) {
      return store.CurrentGame.interlude.text.all;
    } else {
      if (store.CurrentGame.interlude.text.rounds[activeRound.value]) {
        return store.CurrentGame.interlude.text.rounds[activeRound.value];
      }
    }
  } else if (showDemo.value) {
    return 'Example';
  } else if (showingAnswers.value && questions.value[activeRound.value][activeQuestion.value].answerData.text) {
    return questions.value[activeRound.value][activeQuestion.value].answerData.text;
  }
  if (questions.value[activeRound.value] && questions.value[activeRound.value][activeQuestion.value] && questions.value[activeRound.value][activeQuestion.value].category) {
    return questions.value[activeRound.value][activeQuestion.value].category;
  }
  return '';
})
const categorySubText = computed(() => {
  if (questions.value[activeRound.value] && questions.value[activeRound.value][activeQuestion.value] && questions.value[activeRound.value][activeQuestion.value].categorySubText) {
    return questions.value[activeRound.value][activeQuestion.value].categorySubText;
  }
  return '';
})


watch(questionOverride, async () => {
  questions.value = JSON.parse(questionOverride.value);
});

watch(activeQuestion, async () => {
  saveStorage('CurrentGame', store.CurrentGame);
  // transitionAnswer();
});
watch(activeRound, async () => {
  saveStorage('CurrentGame', store.CurrentGame);
  // transitionAnswer();
});

const scoreboard = computed(() => {
  // Used for saving to local storage. Ignoring timer to avoid over saving.
  return store.CurrentGame.scoreboard;
});

watch(() => scoreboard, () => {
        saveStorage('CurrentGame', store.CurrentGame);
      },
      { deep: true }
);
watch(() => questions, () => {
        saveStorage('CurrentGame', store.CurrentGame);
      },
      { deep: true }
);


const modifierKey = reactive({
  shift: false,
  ctrl: false,
  alt: false,
});
const modifierKeys = computed(() => {
  // Return True if any modifier key is pressed
  return modifierKey.shift || modifierKey.ctrl || modifierKey.alt;
});
const hideEditor = computed(() => {
  return modifierKey.ctrl && modifierKey.alt
});
const keyMemory = reactive({
  questionSubStep: 0,
  questionStep: 0,
  showInterlude: false,
  showManager: false,
});



watch(hideEditor, async (newVal) => {
  if (newVal) {
    keyMemory.showManager = generators.value.manager;
    keyMemory.showInterlude = showInterlude.value;
    keyMemory.questionSubStep = questionSubStep.value;
    keyMemory.questionStep = questionStep.value;
    generators.value.manager = false;
    showInterlude.value = false;
    questionSubStep.value = 99;
    questionStep.value = 99;
  } else {
    generators.value.manager = keyMemory.showManager;
    showInterlude.value = keyMemory.showInterlude;
    questionSubStep.value = keyMemory.questionSubStep;
    questionStep.value = keyMemory.questionStep;
  }
});

function keyupHandler(ev) {
  switch(ev.key) {
    case('Control'): {
      modifierKey.ctrl = false;
      modifierKey.shift = false;
      modifierKey.alt = false;
      break;
    }
    case('Shift'): {
      modifierKey.ctrl = false;
      modifierKey.shift = false;
      modifierKey.alt = false;
      break;
    }
    case('Alt'): {
      modifierKey.alt = false;
      modifierKey.shift = false;
      modifierKey.alt = false;
      /*modifierKey.value = null;
      questionSubStep.value = keyMemory.questionSubStep;
      questionStep.value = keyMemory.questionStep;
      showInterlude.value = keyMemory.showInterlude;*/
      break;
    }
  }
}

async function keydownHandler(ev) {
  const preventKey = (tools.value.scores || store.CurrentGame.showScoreboard || quickEdit.value || (tools.value.questions && !modifierKey.alt)); // Ignore keypresses if question editor or scoreboard is open
    switch(ev.key) {
      case('Control'): {
        modifierKey.ctrl = true;
        break;
      }
      case('Shift'): {
        modifierKey.shift = true;
        break;
      }
      case('Alt'): {
        modifierKey.alt = true;
        /*keyMemory.questionSubStep = questionSubStep.value;
        keyMemory.questionStep = questionStep.value;
        keyMemory.showInterlude = showInterlude.value;
        questionSubStep.value = 99;
        questionStep.value = 99;
        showInterlude.value = false;*/
        break;
      }
      case('a'): {
        store.setAnswer(!showingAnswers.value);
        break;
      }
      case('e'): { // Show demo of game for new players
        if (showDemo.value) tools.value.answers = false; // Guarantee the answer to the actual puzzle doesn't show when exiting demo mode
        if (!preventKey) showDemo.value = !showDemo.value;
        // Trigger a transition between the demo & actual question
        transitionTrigger();
        break;
      }
      case('ArrowRight'): {
        if (!preventKey) {
          if (!showInterlude.value) {
            nextQuestion();
          } else {
            if (activeRound.value === -1) activeRound.value = 0;
            store.setInterlude(false);
          }
        }
        break;
      }
      case('ArrowLeft'): {
        if (!preventKey) {
          if (!showInterlude.value) {
            previousQuestion();
          } else {
            store.setInterlude(false);
          }
        }
        break;
      }
      case('t'): {
        if (!preventKey) {
          store.setTimer({ timer: !store.CurrentGame.timer });
        }
        break;
      }
      case('`'): {
        if (!preventKey && !tools.value.questions && !tools.value.scores) {
          tools.value.questions = true;
          generators.value.manager = true;
        } else if (!tools.value.scores) closeTools();
        break;
      }
      case('Enter'): {
        if (!preventKey && !store.CurrentGame.showScoreboard) {
          store.showScoreboard();
          break;
        }
      }
      case('='): {
        if (store.CurrentGame.showScoreboard) {
          store.CurrentGame.showScoreboard = false;
          store.showScoreboard();
          break;
        }
      }
      //case('q'): {
      //  if (!preventKey) {
      //    resetGenerators();
      //    ev.preventDefault();
      //    tools.value.questions = true;
      //    generators.value.manager = true;
      //    if (store.CurrentGame.interlude.show) {
      //      store.CurrentGame.activeRound = -1;
      //      await nextTick();
      //      if (document.getElementById('interludeTextAllInput')) {
      //        document.getElementById('interludeTextAllInput').focus();
      //      }
      //    }
      //    // activeRound.value = round.value;
      //    // activeQuestion.value = question.value;
      //  }
      //  break;
      //}
      case(' '): {
        if (!preventKey) questionStepForward();
        break;
      }
      case('i'): {
        if (!preventKey) {
          store.setInterlude(!showInterlude.value);
          if (!showInterlude.value && activeRound.value === -1) {
            store.setRound(0, 0);
          }
        }
      }
      case('l'): {
        if (!preventKey) {
          resetGenerators();
          generators.value.wordLeap = true;
        }
        break;
      }
      case('ArrowUp'): {
        if (!preventKey) {
          if (modifierKey.shift) {
            // Increase timer by 15 seconds
            totalTime.value += Number(15);
            timeLeft.value += Number(15);
          }
        }
        break;
      }
      case('ArrowDown'): {
        if (!preventKey) {
          if (modifierKey.shift) {
            // Increase timer by 15 seconds
            totalTime.value -= 15;
            timeLeft.value -= 15;
          }
        }
        break;
      }
      case('1'):
      case('2'):
      case('3'):
      case('4'):
      case('5'):
      case('6'):
      case('7'):
      case('8'):
      case('9'): {
        if (!preventKey) {
          if (!modifierKeys.value) {
            store.setQuestion(Number(ev.key) - 1);
          }
          if (modifierKey.shift) {
            store.setQuestion(Number(ev.key) + 9);
          }
          if (modifierKey.ctrl) {
            store.setRound(Number(ev.key) - 1);
          }
          transitionTrigger();
        }
        break;
      }
      case('0'): {
        if (!preventKey) {
          if (!modifierKeys) {
            store.setQuestion(9);
          }
          if (modifierKey.shift) {
            store.setQuestion(19);
          }
          if (modifierKey.ctrl) {
            store.setRound(9);
          }
          transitionTrigger();
        }
        break;
      }
      default: break;
    }
}

// Watch for keystrokes
onMounted(() => {
  window.addEventListener('keyup', keyupHandler);
  window.addEventListener('keydown', keydownHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('keyup', keyupHandler);
  window.removeEventListener('keydown', keydownHandler);
});

const quickEdit = ref(null);

function handleSegmentClick(event, id, type) {
  event.stopPropagation();
  if (!quickEdit.value) {
    if (type === 'text') {
      quickEdit.value = id;
      nextTick(() => {
        const input = document.getElementById(id);
        if (input) {
          input.focus();
        }
      });
    } else {
      const target = event.target;
      const text = target.textContent;
      let clickX = event.clientX;
      let clickY = event.clientY;
      
      const targetRect = target.getBoundingClientRect();
      const targetX = targetRect.left;
      const targetY = targetRect.top;
      
      clickX -= targetX;
      clickY -= targetY;

      let charIndex = 0;
      
      // Create a temporary span to measure text width
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'pre';
      tempSpan.style.font = window.getComputedStyle(target).font;
      document.body.appendChild(tempSpan);
      
      let lineHeight = 0;
      let foundLine = false;

      for (let i = 0; i < text.length; i++) {
        tempSpan.innerText = text.slice(0, i + 1);
        const spanRect = tempSpan.getBoundingClientRect();
        if (!foundLine && clickY <= spanRect.bottom - targetY) {
          lineHeight = spanRect.height;
          foundLine = true;
        }
        if (foundLine) {
          if (clickX <= spanRect.right) {
            charIndex = i;
            break;
          }
        }
        if (foundLine && spanRect.height > lineHeight) {
          break;
        }
      }
      document.body.removeChild(tempSpan);
      quickEdit.value = id;
      nextTick(() => {
        const input = document.getElementById(id);
        if (input) {
          input.focus();
          console.log('focusing', charIndex);
          input.setSelectionRange(charIndex, charIndex);
        }
      });
    }

    // event.stopPropagation();
    // quickEdit.value = id;
    // // Select the text in the textarea
    // nextTick(() => {
    //   console.log('focusing');
    //   console.log(document.getElementById(id));
    //   document.getElementById(id).focus();
    // });
  }
  
}


function quickEditStyle(segment, index) {
  let style = '';
  style += `background: none;`;
  style += `color: inherit;`;
  style += `font-size: inherit;`;
  style += `width: 100%;`;
  style += `height: 100%;`;
  style += `caret-color:gold;`;
  style += 'text-align: center;';
  return style;
}

const hiddenContainer = ref(null);

const maxHeight = computed(() => {
  const container = hiddenContainer.value;

  // Ensure container exists and has children
  if (container && container.children && container.children.length > 0) {
    const heights = Array.from(container.children).map(c => c.offsetHeight);
    // Return the maximum height of any child
    return `height: ${Math.max(...heights)}px`;
  }

  // Return an empty height if the container or children don't exist
  return '';
});


const connectToRemote = computed({
  get() {
    return store.connectToRemote;
  },
  set(newVal) {
    store.connectToRemote = newVal;
  }
});

watch(connectToRemote, async (newVal) => {
  console.log('connectToRemote', newVal);
  connectToRemote.value = newVal;
  if (newVal) {
    initWebSocket();
  }
});


// Hide mouse cursor after a delay
const hideCursor = ref(false);
const hideCursorTimeout = ref(null);

function handleMouseMove() {
  // Clear any existing timeout when the mouse is moving
  clearTimeout(hideCursorTimeout.value);
  hideCursor.value = false;
  // Set a timeout to hide the cursor after a delay
  hideCursorTimeout.value = setTimeout(() => {
    hideCursor.value = true;
  }, 1000);
}

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  clearTimeout(hideCursorTimeout);
});


</script>

<template>
  <div
      id="app-container"
      class="app-container"
      :style="questions[activeRound] && questions[activeRound][activeQuestion] && questions[activeRound][activeQuestion].format && questions[activeRound][activeQuestion].format.background ? `background: ${questions[activeRound][activeQuestion].format.background}` : ''"
      @touchstart="startTouch"
      @touchend="finishTouch">
    <!-- Questions -->
    <Transition
        name="fade"
        mode="out-in">
      <div
          id="category-intro"
          v-if="categoryIntro"
          class="category-intro">
        <div
            class="category-intro__text"
            v-text="categoryText"
        />
        <div
            class="category-intro__subtext"
            v-text="categorySubText"
        />
        <div
            class="category-intro__points"
            v-if="currentQuestion && questions[activeRound][activeQuestion].points">
           {{questions[activeRound][activeQuestion].points}} Points
        </div>
      </div>
    </Transition>
    <div
        class="trivia-container"
        @click="quickEdit = null">
      <Transition
          name="fade"
          mode="out-in">
        <div
            id="question"
            class="question"
            key="question"
            v-if="!hideQuestion && !showInterlude">
          <div
              v-if="!categoryIntro
              && questions[activeRound]
              && questions[activeRound][activeQuestion]
              && questions[activeRound][activeQuestion].segments
              && questions[activeRound][activeQuestion].segments.length"
              v-for="(segment, index) in questions[activeRound][activeQuestion].segments"
              :key="index">
            <Transition
                name="fade"
                mode="out-in">
              <div
                  :id="`segment-container-${segment._id}-${index}`"
                  v-if="segment.meta.display"
                  :class="segmentClass(segment.type)"
                  :style="segment.showOnAnswer && !showingAnswers ? 'display:none' : ''">
                <div
                    v-if="segment.type === 'text'"
                    :style="segment.magnify > 0 ? `font-size: ${segment.magnify}em` : ''"
                    @click="handleSegmentClick($event, `segment-${segment._id}-${index}`, 'text')">
                  <div
                      v-if="quickEdit !== `segment-${segment._id}-${index}`"
                      v-html="segment.data"
                  />
                  <div
                      v-else>
                    <button
                        @click="segment.magnify += 0.15">
                      +
                    </button>
                    <button
                        @click="segment.magnify -= 0.15">
                      -
                    </button>
                    <ckeditor
                        :editor="editor"
                        :config="editorConfig"
                        :id="`segment-${segment._id}-${index}`"
                        :style="quickEditStyle(segment, index)"
                        v-model="segment.data"
                        @blur="quickEdit = null"
                    />
                  </div>
                </div>
                <div
                    v-if="segment.type === 'bet'">
                  <small
                      class="bet-text">
                    <strong>Bet {{segment.data.amount}}:</strong>
                    For this question, you can wager <strong>{{segment.data.amount > 1 ? 'up to' : '' }} {{segment.data.amount}} {{segment.data.amount > 1 ? 'points' : 'point'}}</strong>
                  </small>
                </div>
                <div 
                    v-else-if="segment.type === 'image'"
                    :class="segment.showOnAnswer ? 'question-segment__show-on-answer' : ''">
                  <Transition
                      name="fade-delay">
                    <!-- TODO Need to change this. Add dedicated wordwise category to show demo -->
                    <img
                        :src="!showDemo ? segment.link : wordWiseDemo"
                        :style="segment.magnify > 0 ? `width: ${segment.magnify}%` : ''"
                        v-if="!segment.showOnAnswer || (segment.showOnAnswer && showingAnswers)"
                    />
                  </Transition>
                </div>
                <div
                    v-else-if="segment.type === 'audio'">
                  <audio controls>
                    <source
                        :src="segment.link">
                  </audio>
                </div>
                <div
                    v-else-if="segment.type === 'video'">
                  <video id="videoPlayer" controls>
                    <source
                        :src="segment.link">
                  </video>
                </div>
                <div
                    v-else-if="segment.type === 'blankFill'"
                    id="blankFill">
                  <div
                      v-for="(word, index) in segment.data"
                      :key="index"
                      class="fill-in-blank">
                    <fill-in-blank
                        :key="index"
                        :answers="showingAnswers"
                        :word="word"
                        :index="index"
                        :timeLeft="timeLeft"
                        :totalTime="Number(totalTime)"
                        :hint="segment.dataAnswers[index]"
                        :magnify="segment.magnify"
                        :format="questions[activeRound][activeQuestion].format"
                        type="blankFill"
                    />
                  </div>
                </div>
                <div
                    v-else-if="segment.type === 'wordLeap'"
                    id="wordLeap">
                  <div
                      v-for="(word, index) in !showDemo ? segment.data : wordLeapSample"
                      :key="index">
                    <fill-in-blank
                        :answers="showingAnswers || index === 0 || (!showDemo ? index === segment.data.length - 1 : index === wordLeapSample.length - 1)"
                        :word="word"
                        :index="index"
                        :timeLeft="timeLeft"
                        :totalTime="Number(totalTime)"
                        :hint="segment.dataAnswers[index]"
                        type="wordLeap"
                        :magnify="segment.magnify"
                        :format="questions[activeRound][activeQuestion].format"
                    />
                  </div>
                </div>
                <div
                    v-else-if="segment.type === 'multipleChoice'">
                  <!-- Display multiple choice -->
                  <div
                      v-if="questionSubStep > -1"
                      class="multiple-choice__container">
                    <div
                        v-for="(option, mcIndex) in multipleChoiceDisplay(index)"
                        :key="mcIndex">
                      <Transition
                          name="fade"
                          mode="out-in">
                        <div
                            :id="`multiple-choice-container-${segment._id}-${index}-${mcIndex}`"
                            v-if="index < questionSubStep"
                            class="trivia-multiple-choice"
                            :class="`trivia-multiple-choice__${multipleChoiceClass(index, option, mcIndex)}`"
                            @click="handleSegmentClick($event, `multiple-choice-${segment._id}-${index}-${mcIndex}`)">
                            <p
                              v-if="quickEdit !== `multiple-choice-${segment._id}-${index}-${mcIndex}`"
                              :style="segment.magnify > 0 ? `font-size: ${segment.magnify}em` : ''"
                              v-html="option"
                            />
                            <div
                                v-else>
                              <input
                                  :id="`multiple-choice-${segment._id}-${index}-${mcIndex}`"
                                  v-model="segment.data[mcIndex]"
                                  @blur="quickEdit = null"
                                  :style="quickEditStyle(segment, mcIndex)"
                              />
                            </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </div>
                <div
                    v-else-if="segment.type === 'clueAnswer'">
                  <!-- Display 'Clues & Answers' -->
                  <div>
                    <div
                        :style="`display: grid; ${segment.topDown ? 'grid-auto-flow: column' : 'grid-auto-flow: row'}; grid-template-columns: repeat(${segment.columns}, 1fr); grid-template-rows: repeat(${Math.ceil(segment.data.length / segment.columns)}, 1fr);`">
                      <div
                          v-for="(option, mcIndex) in multipleChoiceDisplay(index)"
                          class="clue-answer__outer-container"
                          :key="mcIndex">
                        <Transition
                          name="fade"
                          mode="out-in">
                          <div
                              v-if="segment.meta.displaySubsegment > mcIndex">
                            <div
                                class="clue-answer__container"
                                :id="`clue-answer-${segment._id}-${index}-${mcIndex}`"
                                :style="maxHeight">
                              <!-- :class="`trivia-multiple-choice__${multipleChoiceClass(key, option, index)}`"> -->
                              <div
                                  key="indexAndAnswer"
                                  v-if="(!segment.hideIndex || (segment.forceAnswer && showingAnswers)) && !segment.reverseIndex"
                                  class="clue-answer__answer"
                                  :class="showingAnswers ? 'clue-answer__answer__active' : ''"
                                  :style="clueAnswerStyle(segment, (showingAnswers && option.answer) ? 'answer' : 'index', mcIndex)">
                                <Transition
                                    name="fade"
                                    mode="out-in">
                                  <span
                                      v-if="showingAnswers && option.answer"
                                      key="answer"
                                      v-html="option.answer"
                                  />
                                  <span
                                      v-else
                                      key="index"
                                      v-html="option.indexLabel ? option.indexLabel : `#${mcIndex + 1}`"
                                  />
                                </Transition>
                              </div>
                              <div
                                  key="clue"
                                  @click="handleSegmentClick($event, `clue-answer-clue-${segment._id}-${index}-${mcIndex}`)"
                                  class="clue-answer__clue"
                                  :style="clueAnswerStyle(segment, 'clue', mcIndex)">
                                <span
                                    v-if="quickEdit !== `clue-answer-clue-${segment._id}-${index}-${mcIndex}`"
                                    :style="option.format.magnify > 0 ? `font-size: ${(segment.magnify * option.format.magnify)}em` : ''"
                                    v-html="option.clue"
                                />
                                <div
                                    v-else>
                                  <input
                                      :id="`clue-answer-clue-${segment._id}-${index}-${mcIndex}`"
                                      :style="quickEditStyle(segment, mcIndex)"
                                      v-model="segment.data[mcIndex].clue"
                                      @blur="quickEdit = null"
                                  />
                                </div>
                              </div>
                              <div
                                  key="indexAndAnswer"
                                  v-if="(!segment.hideIndex || (segment.forceAnswer && showingAnswers)) && segment.reverseIndex"
                                  class="clue-answer__answer"
                                  :class="showingAnswers ? 'clue-answer__answer__active' : ''"
                                  :style="clueAnswerStyle(segment, showingAnswers ? 'answer' : 'index', mcIndex)">
                                <Transition
                                    name="fade"
                                    mode="out-in">
                                  <span
                                      v-if="showingAnswers && option.answer"
                                      key="answer"
                                      v-html="option.answer"
                                  />
                                  <span
                                      v-else
                                      key="index"
                                      v-html="option.indexLabel ? option.indexLabel : `#${mcIndex + 1}`"
                                  />
                                </Transition>
                              </div>
                            </div>
                          </div>
                        </Transition>
                      </div>
                    </div>
                    <!-- hidden div to measure height -->
                    <div
                        id="hidden-container"
                        ref="hiddenContainer"
                        :style="`display: grid; ${segment.topDown ? 'grid-auto-flow: column' : 'grid-auto-flow: row'}; grid-template-columns: repeat(${segment.columns}, 1fr); grid-template-rows: repeat(${Math.ceil(segment.data.length / segment.columns)}, 1fr);`"
                        class="hidden-container">
                      <div
                          v-for="(option, mcIndex) in multipleChoiceDisplay(index)"
                          class="clue-answer__outer-container"
                          :key="mcIndex">
                        <div>
                          <div
                              class="clue-answer__container"
                              :id="`clue-answer-${segment._id}-${index}-${mcIndex}`">
                              <!-- :class="`trivia-multiple-choice__${multipleChoiceClass(key, option, index)}`"> -->
                            <div
                                key="indexAndAnswer"
                                v-if="(!segment.hideIndex || (segment.forceAnswer && showingAnswers)) && !segment.reverseIndex"
                                class="clue-answer__answer"
                                :class="showingAnswers ? 'clue-answer__answer__active' : ''"
                                :style="clueAnswerStyle(segment, (showingAnswers && option.answer) ? 'answer' : 'index', mcIndex)">
                            </div>
                            <div
                                key="clue"
                                class="clue-answer__clue"
                                :style="clueAnswerStyle(segment, 'clue', mcIndex)">
                              <span
                                  v-if="quickEdit !== `clue-answer-clue-${segment._id}-${index}-${mcIndex}`"
                                  :style="option.format.magnify > 0 ? `font-size: ${(segment.magnify * option.format.magnify)}em` : ''"
                                  v-html="option.clue"
                              />
                              <div
                                  v-else>
                                <input
                                    :id="`clue-answer-clue-${segment._id}-${index}-${mcIndex}`"
                                    :style="quickEditStyle(segment, mcIndex)"
                                    v-model="segment.data[mcIndex].clue"
                                />
                              </div>
                            </div>
                            <div
                                key="indexAndAnswer"
                                v-if="(!segment.hideIndex || (segment.forceAnswer && showingAnswers)) && segment.reverseIndex"
                                class="clue-answer__answer"
                                :class="showingAnswers ? 'clue-answer__answer__active' : ''"
                                :style="clueAnswerStyle(segment, showingAnswers ? 'answer' : 'index', mcIndex)">
                              <span
                                  v-if="showingAnswers && option.answer"
                                  key="answer"
                                  v-html="option.answer"
                              />
                              <span
                                  v-else
                                  key="index"
                                  v-html="option.indexLabel ? option.indexLabel : `#${mcIndex + 1}`"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                    v-else-if="segment.type === 'crossword'">
                    <!-- TODO -- Add handling for toggleable hints -->
                    <crossword-puzzle
                        :words="segment.data"
                        :showAnswers="showingAnswers"
                        :showHints="false"
                        :totalTime="Number(totalTime)"
                        :timeLeft="Number(timeLeft)"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'wordHunt'">
                  <word-hunt
                      :gridLength="segment.gridCount"
                      :gridHeight="segment.gridCount"
                      :gridLetters="segment.data"
                      :showLetters="true"
                      :minLength="segment.minLength"
                      :maxPoints="teams.length"
                      :type="'wordHunt'"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'anagramScramble'">
                  <word-hunt
                      :gridLength="segment.gridLength"
                      :gridHeight="segment.gridHeight"
                      :gridLetters="segment.data"
                      :showLetters="true"
                      :minLength="segment.minLength"
                      :maxPoints="teams.length"
                      :type="'anagramScramble'"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'wordFrag'">
                  <word-cards
                      :q="segment"
                      :totalTime="Number(totalTime)"
                      :timeLeft="Number(timeLeft)"
                      :begin="timerStarted"
                      :maxPoints="teams.length"
                      :answer="showingAnswers"
                      type="wordFrag"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'synonymScramble'">
                  <word-cards
                      :q="segment"
                      :totalTime="Number(totalTime)"
                      :timeLeft="Number(timeLeft)"
                      :begin="timerStarted"
                      :maxPoints="teams.length"
                      :answer="showingAnswers"
                      type="synonymScramble"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'secretWord'">
                  <word-cards
                      :type="'secretWord'"
                      :q="segment"
                      :totalTime="Number(totalTime)"
                      :timeLeft="Number(timeLeft)"
                      :begin="timerStarted"
                      :maxPoints="teams.length"
                      :answer="showingAnswers"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'categoryCrush'">
                  <word-cards
                      :type="'categoryCrush'"
                      :q="segment"
                      :totalTime="Number(totalTime)"
                      :timeLeft="Number(timeLeft)"
                      :begin="timerStarted"
                      :maxPoints="teams.length"
                      :answer="showingAnswers"
                  />
                </div>
                <div
                    v-else-if="segment.type === 'alphabetCrush'">
                  <word-cards
                      :type="'alphabetCrush'"
                      :q="segment"
                      :totalTime="Number(totalTime)"
                      :timeLeft="Number(timeLeft)"
                      :begin="timerStarted"
                      :maxPoints="teams.length"
                      :answer="showingAnswers"
                  />
                </div>
                <!-- <div
                    v-else-if="detail.memorySquares">
                    <div
                        v-for="(square, index) in detail.memorySquares.squares"
                        class="memory-square"
                        :key="index"
                        :class="activeSquare !== index ? `${square}-inactive` : square">
                    </div>
                    <memory-squares
                      :squares="detail.squares"
                      :sequence="detail.sequence"
                  />
                </div> -->
              </div>
            </Transition>
          </div>
        </div>
        <div
            v-else-if="showInterlude"
            key="interlude"
            class="question">
          <img
              v-if="store.CurrentGame.interlude.image && store.CurrentGame.interlude.image.link"
              class="interlude-image"
              :src="store.CurrentGame.interlude.image.link"
          />
        </div>
      </Transition>
    </div>
    <div
        class="info-bar"
        v-if="!categoryIntro"
        :style="progressBarStyle">
      <Transition
          name="answer-fade"
          mode="out-in">
        <div
            class="category-container">
          <div
              class="trivia-round"
              v-if="!showInterlude">
            <span
                class="trivia-round__text">
              <small>R{{Number(activeRound) + 1}}</small> Q{{Number(activeQuestion) + 1}}
              <br />
              <span
                  v-if="questions[activeRound][activeQuestion].points">
                {{questions[activeRound][activeQuestion].points}} Points
              </span>
            </span>
          </div>
          <Transition
              name="fade">
            <div
                v-if="!transitionCategory && categoryText"
                class="trivia-category">
            <!-- Category Display  -->
              <span
                  id="category"
                  v-if="categoryText && (questions
                  && questions[activeRound]
                  && questions[activeRound][activeQuestion]
                  && questions[activeRound][activeQuestion].category)
                  || showInterlude
                  || showDemo"
                  :class="categoryText.length > 35
                    ? 'category-text__small'
                    : categoryText.length > 25
                      ? 'category-text__medium'
                      : ''"
                  v-html="categoryText"
              />
            </div>
          </Transition>
          <!-- Timer -->
          <div
              class="timer"
              v-html="timeLeftInMinutes"
              v-if="!showInterlude">
          </div>
        </div>
      </Transition>
    </div>
    <Transition
        mode="out-in"
        name="fade">
      <team-scores
          v-if="store.CurrentGame.showScoreboard"
          @close="store.showScoreboard()"
      />
    </Transition>
    <!-- Question Manager -->
    <active-game-editor
          v-if="tools.questions && !hideEditor"
          class="manager"
          @update="updateQuestions"
          @close="closeTools()"
          @manager="router.push('/manager')"
    />
  </div>
</template>

<style
    lang="scss">
@import '@/assets/base.css';



#app {
  background: rgb(0,0,0);
  margin: 0 !important;
  padding: 0 !important;
}
.app-container {
  /* background: rgb(75,97,84);
  background: radial-gradient(circle, rgba(75,97,84,1) 0%, rgba(64,96,120,1) 100%); */
  color: white;
  margin: 0;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(350deg, rgba(255,255,255,1) 0%, rgba(73,179,247,1) 12%, rgba(217,213,77,1) 37%, rgba(80,88,193,1) 57%, rgba(159,227,210,1) 76%, rgba(225,250,255,1) 92%);
  background-size: 400% 400%;
  animation: swirl 30s ease-in-out infinite;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  transition: all 1s ease;
}

@keyframes swirl {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

strong {
  font-weight: bold;
  color: gold !important;
}

img {
  margin: auto;
  border-radius: 10px;
  display: flex;
}
/* .option-image {
  /* width: 21vw !important;
  height: 21vh !important;
} */
video {
  max-width: 90vw;
  max-height: 85vh;
  border: black solid 1px;
  border-radius: 15px;
  margin: auto;
  display: flex;
}


/*h2, .question-segment {
  font-size: 2.5em;
}
h3, .question-segment {
  font-size: 1.5em;
  line-height: 1.4em;
}
h4, .question-segment {
  font-size: 0.75em;
  line-height: 1.2em;
}*/



.answer-fade-enter-active {
  transition: all 0.7s ease-out;
}
.answer-fade-leave-active {
  transition: all 0.5s ease-out;
}
.answer-fade-enter-from {
  transform: translateX(-50vw);
  opacity: 0;
}
.answer-fade-leave-to {
  transform: translateX(50vw);
  opacity: 0;
}

.category-fade-enter-active {
  transition: all 0.7s ease-out;
}
.category-fade-leave-active {
  transition: all 0.5s ease-out;
}
.category-fade-enter-from {
  transform: translateX(-45vw);
}
.category-fade-leave-to {
  transform: translateX(-70vw);
}


.slide-fade-enter-active {
  transition: all 0.6s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-enter-active, .slide-center-enter-active, .slide-left-enter-active {
  transition: all 0.6s ease-out;
}

.slide-right-leave-active, .slide-center-leave-active, .slide-left-leave-active {
  transition: all 0.6s ease-out;
}

.slide-right-enter-from {
  transform: translateX(-50px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(50px);
  opacity: 0;
}
.fade-enter-active {
  transition: all 0.6s ease-out;
}

.fade-leave-active {
  transition: all 0.6s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-delay-enter-active {
  transition-delay: 2s;
  transition: all 1.5s ease-out;
}

.fade-delay-leave-active {
  transition: all 1.5s ease-out;
}

.fade-delay-enter-from,
.fade-delay-leave-to {
  opacity: 0;
}

/* Trivia Night */

/* #question-container {
  position: relative;
} */

.small-button {
  font-size: 0.8em;
  margin-right: 1em;
}

.trivia-container {
  height: 87vh;
  width: 100vw;
  position: absolute;
  /* display: flex; */
  top: 0;
  right: 0;
  font-size: 3em;
  /* background: inherit; */
  text-align: center;
  align-items: center;
  /* top: 50%;
  text-align: center;
  height: 90vh;
  width: 100vw; */
  /* position: absolute;
  top: 0;
  right: 0;
  height: 90vh;
  width: 100vw; */
  /* font-size: 7vmin;
  padding-bottom: 10vh; */
  /* margin-top: auto;
  margin-bottom: auto; */
}

.trivia-container::before {
  content: '';
  display: inline-block;
  height: 100%; 
  vertical-align: middle;
  /* margin-right: -0.25em; Adjusts for spacing */

  /* For visualization 
  background: #808080; width: 5px;
  */
}

.question {
  display: inline-block;
  vertical-align: middle;
  width: 100vw;
}



.clue-answer {
  &__outer-container {
    display: grid;
    gap: 10px;
    margin: 0.5vh 0.5vw;
  }
  &__container {
    border-radius: 28px;
    border: 3px solid rgba(black, 0.8);
    box-shadow: 4px 4px 4px rgba(black, 0.6);
    display: flex;
    height: 100%;
    align-content: center;
    justify-content: center;
  }
  &__answer {
    flex: 1;
    align-content: center;
    justify-content: center;
    background-color: darken(skyblue, 30%);
    color: white;
    transition: all 1.5s ease;
    /* text-shadow: 2px 2px 2px rgba(black, 0.8); */
    /* flex: 1; */
    /* &__active {
      transition: all 0.5s ease;
      background-color: darken(skyblue, 10%);
      color: white;
      text-shadow: 2px 2px 2px rgba(black, 0.8);
    } */
  }
  &__clue {
    display: flex;
    flex: 1;
    background-color: darken(skyblue, 40%);
    color: white;
    align-items: center;
    justify-content: center;
    padding: 5px;
    /* flex: 2; */
    /* text-shadow: 2px 2px 2px rgba(black, 0.8); */
  }
}

.trivia-multiple-choice {
  background-color: darken(azure, 30%);
  color: black;
  border: solid gold 2px;
  padding: 10px;
  margin: 1vh 1vw;
  border-radius: 5px;
  font-size: 0.85em;
  &__correct {
    /* background-color: gold; */
    color: darkslategrey;
    transition: all ease 1.1s;
  }
  &__incorrect {
    background-color: darkslategrey;
    color: lighten(darkslategrey, 50%);
    transition: all ease 1.1s;
  }
  &__0 {
    background-color: #fcd303;
    color: darken(#fcd303, 51%);
  }
  &__1 {
    background-color: #03fc77;
    color: darken(#03fc77, 81%);
  }
  &__2 {
    background-color: #03f8fc;
    color: darken(#03f8fc, 51%);
  }
  &__3 {
    background-color: lighten(#fc0341, 25%);
    color: darken(#fc0341, 41%);
  }
  &__4 {
    background-color: #1ab7e7;
    color: darken(#21cbff, 51%);
  }
  &__5 {
    background-color: #e863eb;
    color: darken(#e863eb, 81%);
  }
  &__6 {
    background-color: #e3a242;
    color: darken(#e3a242, 81%);
  }
  &__7 {
    background-color: #2cbf38;
    color: darken(#2cbf38, 51%);
  }
  &__8 {
    background-color: #f5315f;
    color: darken(#f5315f, 51%);
  }
  &__9 {
    background-color: #0b03fc;
    color: lighten(#0b03fc, 51%);
  }
  &__10 {
    background-color: #eeff00;
    color: darken(#eeff00, 51%);
  }
  &__11 {
    background-color: #9c0202;
    color: lighten(#9c0202, 81%);
  }
}

/* .trivia-container {
  height: 100vh;
} */



.question-segment {
  padding: 0.5vmax;
  background-color: rgba(black, 0.65);
  border-radius: 5px;
  /* border: 5px solid rgba(35,49,125,1); */
  box-shadow: 2px 2px 5px 2px rgba(0,0,0,0.85);
  margin: 1vh 2vw;
  &__image {
    background: none;
    border-radius: 5px;
  }
  &__no-background {
    @extend .question-segment;
    box-shadow: none;
    background: none;
  }
  &__show-on-answer {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -76vh;
    margin-left: -35vw;
    /* background: rgba(black, 0.8); */
    padding: 10vh 15vw;
  }
}

.answer-container {
  background-color: black;
  height: 12vh;
  bottom: 0vh;
  display: grid;
  justify-content: center;
  align-content: center;
}
.category-intro {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  /* color: black; */
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.7);
  /* background: radial-gradient(circle, #e9ff26, #ebeb34); */
  /* border: 60px solid rgba(#0472e0, 0.6); */
  &__text {
    font-weight: 800;
    font-size: 6em;
    padding-bottom: 1.5vh;
    text-transform: uppercase;
  }
  &__subtext {
    font-size: 4em;
    font-weight: 800;
    padding-bottom: 1.5vh;
    font-style: italic;
  }
  &__points {
    font-size: 3em;
    font-weight: 800;
    font-style: italic;
    padding: 20px;
    border-radius: 10px;
  }
}
.category-text {
  /* font-size: 6em;
  padding-bottom: 2vh;
  color: gold; */
  &__medium {
    font-size: 4.5rem;
  }
  &__small {
    font-size: 2.5rem;
  }
  &__x-small {
    font-size: 2.2rem;
  }
}

.bet-text {
  font-size: 0.5em;
  margin: 10px;
  padding: 20px;
}

/* .info {
  color: lighten(gold, 15%);
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.3);
} */

.info-bar {
  position: fixed;
  bottom: 0vh;
  box-shadow: 5px 5px 5px rgba(0, 0, 0, 0.6);
  text-shadow: 1px 1px 5px rgba(black, 0.8);
  height: calc(11vh + 1px);
  width: 100vw;
  left: 0vw;
  transition: all 1s ease;
}
.category-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 2vh;
  margin: auto;
  height: calc(13vh + 1px);
}


.trivia-round {
  display: inline-flex;
  flex: 0.25;
  text-align: left;
  /* margin-left: 5vw;
  margin-top: 4vh; */
  line-height: 5vh;
  margin-left: 1.5vw;
  margin-top: 3vh;
  height: inherit;
  &__text {
    font-size: 3vw;
  }
}
.trivia-category {
  flex: 1;
  font-size: 5vw;
  font-weight: 800;
  text-align: center;
  overflow: hidden;
  text-align: center;
}

.timer {
  align-items: center;
  flex: 0.35;
  text-align: right;
  margin-right: 1.5vw;
  font-size: 4vw;
}

.countdown {
  display: flex;
  height: 100vh;
  width: 100vw;
  bottom: 0;
  left: 0;
}


.memory-square {
    width: 20vw;
    height: 15vh;
    margin: 2vmax auto;
    border-radius: 5px;
    border: 5px solid white;
  }
  .blue {
    background-color: blue;
  }
  .blue-inactive {
    background-color: rgba(blue, 0.3);
  }
  .red {
    background-color: red;
  }
  .red-inactive {
    background-color: rgba(red, 0.3);
  }
  .yellow {
    background-color: yellow;
  }
  .yellow-inactive {
    background-color: rgba(yellow, 0.3);
  }
  .white {
    background-color: white;
  }
  .white-inactive {
    background-color: rgba(white, 0.3);
  }

.hidden-container {
  display: none;
  position: absolute;
  left: -1000vw;
  top: -1000vh;
}
  

  .scoreboard {
    position: fixed;
    bottom: 0vh;
    width: 100vw;
    background: silver;
    transition: all 1s ease;
    margin: auto;
    cursor: default;
  }
  .teams {
    &__name {
      font-size: 3em;
      width: 60vw;
    }
    &__score {
      margin-left: 1vw;
      margin-right: 2vw;
      font-size:4em;
      line-height: 0;
      border: 2px solid azure;
      background: rgb(75,97,84);
      background: radial-gradient(circle, rgba(75,97,84,1) 0%, rgba(119,120,64,1) 100%);
      color: white;
      padding: 0px 25px;
      &__high {
        color: gold;
        font-weight: 800;
      }
    }
    &__controls {
      display: inline-block;
      width: 15vw;
      &__toAdd {
        width: 5vw;
      }
    }
  }

  .ai {
    &__wordSet {
      display: block;
      margin: 1em;
    }
    &__word {
      display: inline-block; 
      margin: 0.5em;
      background: cornflowerblue;
      padding: 0.5em;
      color: khaki;
      border-radius: 5px;
      cursor: pointer;
    }
  }
  .highlight {
      background-color: rgba(gold, 0.7);
      color: darkslategrey;
  }
  .manager {
    position: absolute;
    text-align: center;
    overflow: scroll;
    z-index: 99;
    top: 0;
    width: 100vw;
    &__header {
      background-color: darkslategrey;
      border-radius: 10px;
      font-size: 2em;
      margin-left: auto;
      margin-right: auto;
      padding: 0.5em 0px;
    }
    &__rounds {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: 1fr;
      grid-column-gap: 0px;
      grid-row-gap: 0px;
      text-align: center;
      margin-top: 25px;
      &__sidebar {
        background: rgba(darkslategrey, 0.8);
        font-size: 2em;
        padding: 1vmax;
        margin-right: 10px;
        margin-left: auto;
        border-radius: 5px;
        width: 350px;
      }
      &__questions {
        margin: auto;
        margin-left: 10px;
      }
      &__question {
        padding: 8px;
        cursor: pointer;
      }
    }
    /* &__question-container {
      width: 50vw;
      margin-top: 15px;
      background: darkslategrey;
      display: flex;
      justify-content: center;
    } */
    /* &__segment-container {
      padding: 5px;
      background: khaki;
      color: black;
      width: 48vw;
    } */
    &__segment {
      background: darken(khaki, 20%);
      margin: 10px;
      padding: 10px;
      border-radius: 5px;
      border: 2px solid darkslategrey;
      &__select {
        margin-right: 10vw;
      }
    }
    &__segment-input {
      min-width: 50vw;
      min-height: 10vh;
    }
    &__question-number {
      background-color: azure;
      color: black;
      padding: 5px;
      border-radius: 5px;
      margin: 10px;
    }
  }

  .ck-content {
     color: black;
  }

.fill-in-blank {
  margin: 8px;
  border-radius: 5px;
  cursor: pointer;
}

.interlude-image {
  max-height: 95vh;
  max-width: 95vw;
}
/* End Trivia Night */
</style>
